import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties, DragEvent, ChangeEvent } from "react";
import { useParams } from "@tanstack/react-router";
import {
  getElectricalMarkers,
  getElectricalOutput,
  getStructuralFrame,
  getMepDevices,
  getMepDucts,
  getMepEquipment,
  getMepFittings,
  getMechanicalOutput,
  getMepTerminals,
  getModelFragments,
  getModelStoreys,
  getSpaces,
  listMepSystems,
} from "../api/client";
import type { ApiSpace, ApiStorey, MepEquipment, MepSystem } from "../api/types";
import { assignDuctStoreys, assignFittingStoreys } from "../mep/ducts";
import { RACEWAY_COLORS, conduitToLayer, pullBoxToLayer } from "../mep/raceways";
import { footprintCentroid } from "../viewer/align";
import { equipmentMeshSpec } from "../viewer/DuctLayer";
import { Viewer, type AlignmentSpace } from "../viewer/Viewer";
import { parseIfcFile } from "../viewer/ifc/parseIfc";
import type { ModelSummary, ParseProgress } from "../viewer/ifc/types";
import { storeysWithSpaces } from "../wizard/plan";

const PHASE_LABEL: Record<ParseProgress["phase"], string> = {
  opening: "Opening IFC…",
  extracting: "Extracting storeys & spaces…",
  converting: "Converting to Fragments…",
};

const panelSection: CSSProperties = {
  background: "#21213a",
  borderRadius: 8,
  padding: "0.75rem",
  marginBottom: "0.75rem",
};

const sectionTitle: CSSProperties = {
  margin: "0 0 0.5rem 0",
  fontSize: "0.75rem",
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  color: "#8e8eb8",
};

/** Latest completed system of one discipline (newest `generated_at` wins). */
function latestComplete(systems: MepSystem[], discipline: string): MepSystem | null {
  return (
    systems
      .filter((s) => s.discipline === discipline && s.status === "complete")
      .sort((a, b) => (b.generated_at ?? 0) - (a.generated_at ?? 0))[0] ?? null
  );
}

/** MEP overlay counts shown in the server-model panel. */
interface OverlayCounts {
  devices: number;
  ducts: number;
  terminals: number;
  equipment: number;
  electrical: number;
  /** Routed raceway segments (opt-in electrical conduit routing). */
  conduits: number;
  /** Pull/junction boxes on the raceways. */
  pullBoxes: number;
  /** Distinct raceway kinds present, for the legend (sorted). */
  conduitKinds: string[];
  /** Placed mechanical equipment glyphs (RTU/AHU/pump/VAV). */
  mechanical: number;
  /** Placed structural columns. */
  columns: number;
  /** Placed structural beams + girders. */
  beams: number;
}

/**
 * Viewer page — Three.js canvas plus the IFC model panel.
 *
 * The `Viewer` class runs OUTSIDE React (spec §7). React owns only the canvas
 * DOM lifecycle and the `ModelSummary` shown in the left panel; scene/model
 * commands go through imperative `Viewer` methods.
 *
 * Two entry modes share the page:
 *  - `/viewer` — local file mode: parse a dropped/picked .ifc in the worker.
 *  - `/models/$modelId/viewer` — server mode: stream the stored Fragments
 *    binary by id and overlay the latest generated MEP systems (fire-alarm
 *    devices + HVAC ductwork) on the model.
 */
export function ViewerPage() {
  const params = useParams({ strict: false }) as { modelId?: string };
  const modelId = params.modelId ?? null;

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const viewerRef = useRef<Viewer | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [summary, setSummary] = useState<ModelSummary | null>(null);
  const [progress, setProgress] = useState<ParseProgress | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeStorey, setActiveStorey] = useState<number | null>(null);
  const [dragging, setDragging] = useState(false);

  // Server mode (load-by-id) state.
  const [serverPhase, setServerPhase] = useState<"idle" | "loading" | "ready" | "error">("idle");
  const [serverError, setServerError] = useState<string | null>(null);
  const [serverStoreys, setServerStoreys] = useState<ApiStorey[]>([]);
  const [overlay, setOverlay] = useState<OverlayCounts | null>(null);
  const [equipment, setEquipment] = useState<MepEquipment[]>([]);
  const [devicesVisible, setDevicesVisible] = useState(true);
  const [ductworkVisible, setDuctworkVisible] = useState(true);
  // Equipment glyphs ride the HVAC result (Mechanical is HVAC expanded), so they
  // get their own toggle alongside ductwork rather than a separate scope.
  const [mechanicalVisible, setMechanicalVisible] = useState(true);
  const [racewaysVisible, setRacewaysVisible] = useState(true);
  const [membersVisible, setMembersVisible] = useState(true);
  const [shellVisible, setShellVisible] = useState(true);

  // The storey selector lists only storeys that own at least one space, so a
  // spaceless datum level (e.g. "TOF Footing") never appears as a selectable
  // storey. The raw `summary.storeys` is kept intact for stats/elevations.
  const selectableStoreys = useMemo(
    () =>
      summary
        ? storeysWithSpaces(
            summary.storeys,
            summary.spaces,
            (storey) => storey.id,
            (space) => space.storeyId,
          )
        : [],
    [summary],
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const viewer = new Viewer();
    viewer.mount(canvas);
    viewerRef.current = viewer;
    if (import.meta.env.DEV) {
      // Debug handle for dev tooling; not part of the public surface.
      (window as unknown as Record<string, unknown>).__istariViewer = viewer;
    }

    const observer = new ResizeObserver(([entry]) => {
      if (entry) {
        const { width, height } = entry.contentRect;
        viewer.resize(width, height);
      }
    });
    observer.observe(canvas.parentElement ?? canvas);

    return () => {
      observer.disconnect();
      viewer.dispose();
      viewerRef.current = null;
    };
  }, []);

  // Server mode: stream the stored Fragments binary + overlay generated MEP.
  useEffect(() => {
    if (!modelId) return;
    const viewer = viewerRef.current;
    if (!viewer) return;
    let cancelled = false;

    void (async () => {
      setServerPhase("loading");
      setServerError(null);
      setOverlay(null);
      // Clear stale raceways up front: switching to a model with no electrical
      // system (or no conduit routing) never calls setRaceways, so without this
      // the previous model's raceways would persist behind the new one.
      viewer.setRaceways([], []);
      viewer.setMembers([], [], []);
      try {
        const [fragments, storeys, spaces] = await Promise.all([
          getModelFragments(modelId),
          // Storeys/spaces/MEP are optional decorations — the model alone is
          // useful (spaces feed the overlay alignment below).
          getModelStoreys(modelId).catch(() => [] as ApiStorey[]),
          getSpaces(modelId).catch(() => [] as ApiSpace[]),
        ]);
        if (cancelled) return;
        await viewer.loadModel(fragments, modelId);
        if (cancelled) return;
        setServerStoreys(storeys);

        // Align the MEP overlay to the mesh: plan coords and the Fragments
        // mesh share axes but differ by a per-model translation.
        const elevationByIfcId = new Map(storeys.map((s) => [s.ifc_id, s.elevation]));
        const alignmentSpaces: AlignmentSpace[] = spaces
          .filter((s) => s.ifc_id !== null && s.footprint && s.footprint.length >= 3)
          .map((s) => {
            const [planX, planY] = footprintCentroid(s.footprint!);
            return {
              ifcId: s.ifc_id!,
              planX,
              planY,
              elevation:
                s.storey_ifc_id !== null ? (elevationByIfcId.get(s.storey_ifc_id) ?? null) : null,
            };
          });
        await viewer.alignLayersToModel(alignmentSpaces);
        if (cancelled) return;

        const systems = await listMepSystems(modelId).catch(() => [] as MepSystem[]);
        if (cancelled) return;
        const fire = latestComplete(systems, "fire_alarm");
        const hvac = latestComplete(systems, "hvac");
        const electrical = latestComplete(systems, "electrical");
        const structural = latestComplete(systems, "structural");
        const counts: OverlayCounts = {
          devices: 0,
          ducts: 0,
          terminals: 0,
          equipment: 0,
          electrical: 0,
          conduits: 0,
          pullBoxes: 0,
          conduitKinds: [],
          mechanical: 0,
          columns: 0,
          beams: 0,
        };
        let nextEquipment: MepEquipment[] = [];

        // The device layer holds one point-marker set. Prefer electrical when a
        // completed electrical system exists (that's what the user generated);
        // otherwise show fire-alarm devices. HVAC ductwork overlays separately.
        if (electrical) {
          // Local fallback: a failed electrical-markers fetch must NOT abort the
          // whole load flow (which would blank the model + HVAC/fire overlays).
          // Log and continue with no electrical overlay instead.
          const markers = await getElectricalMarkers(electrical.id).catch((err) => {
            console.error("electrical markers failed to load; continuing", err);
            return [];
          });
          if (cancelled) return;
          // Markers carry their real mounting height (receptacle ~0.46 m,
          // switch ~1.22 m, luminaire = ceiling) so the 3D overlay is
          // height-accurate, matching the Revit convention.
          viewer.setDevices(
            markers.map((m) => ({
              id: m.id,
              kind: m.kind,
              x_m: m.x_m,
              y_m: m.y_m,
              z_m: m.z_m,
              storey_id: m.storey_id,
            })),
          );
          counts.electrical = markers.length;

          // Raceways are opt-in: a non-conduit electrical Output returns empty
          // arrays. A failed fetch must not blank the model — log and skip.
          const raceway = await getElectricalOutput(electrical.id).catch((err) => {
            console.error("electrical raceways failed to load; continuing", err);
            return { conduits: [], pull_boxes: [], raceway_fittings: [] };
          });
          if (cancelled) return;
          const conduits = raceway.conduits ?? [];
          const pullBoxes = raceway.pull_boxes ?? [];
          // Conduits/pull-boxes carry their route's real storey_id, and z_m is
          // storey-LOCAL on the raw Output. Lift it to world z by the storey
          // elevation (0 when unknown) so raceways stack at the same world
          // levels as the DB-lifted ducts/markers/mesh.
          const elevationByStorey = new Map(storeys.map((s) => [s.id, s.elevation ?? 0] as const));
          const layerConduits = conduits.map((c) =>
            conduitToLayer(c, elevationByStorey.get(c.storey_id) ?? 0),
          );
          const layerPullBoxes = pullBoxes.map((b) =>
            pullBoxToLayer(b, elevationByStorey.get(b.storey_id) ?? 0),
          );
          viewer.setRaceways(layerConduits, layerPullBoxes);
          counts.conduits = conduits.length;
          counts.pullBoxes = pullBoxes.length;
          counts.conduitKinds = [...new Set(conduits.map((c) => c.kind))].sort();
        } else if (fire) {
          const devices = await getMepDevices(fire.id);
          if (cancelled) return;
          viewer.setDevices(devices);
          counts.devices = devices.length;
        }

        // Structural is additive: a model can carry a structural frame
        // alongside any MEP system, so this is NOT part of the else-if chain
        // above. A failed fetch must not blank the model — log and skip.
        if (structural) {
          const frame = await getStructuralFrame(structural.id).catch((err) => {
            console.error("structural frame failed to load; continuing", err);
            return { columns: [], beams: [], lateral: [] };
          });
          if (cancelled) return;
          viewer.setMembers(frame.columns, frame.beams, frame.lateral);
          counts.columns = frame.columns.length;
          counts.beams = frame.beams.length;
        }
        if (hvac) {
          const [ducts, fittings, terminals, hvacEquipment] = await Promise.all([
            getMepDucts(hvac.id),
            getMepFittings(hvac.id),
            getMepTerminals(hvac.id),
            getMepEquipment(hvac.id),
          ]);
          if (cancelled) return;
          const ductStoreys = assignDuctStoreys(ducts, terminals);
          const fittingStoreys = assignFittingStoreys(fittings, ductStoreys, terminals);
          viewer.setDucts(
            ducts.map((d) => ({ ...d, storey_id: ductStoreys.get(d.id) ?? null })),
            fittings.map((f) => ({
              id: f.id,
              kind: f.kind,
              x_m: f.x_m,
              y_m: f.y_m,
              z_m: f.z_m,
              storey_id: fittingStoreys.get(f.id) ?? null,
            })),
            terminals,
            hvacEquipment,
          );
          counts.ducts = ducts.length;
          counts.terminals = terminals.length;
          counts.equipment = hvacEquipment.length;
          nextEquipment = hvacEquipment;
        }
        if (hvac) {
          // Mechanical is HVAC expanded: placed equipment rides on the SAME
          // hvac system as the ductwork above. It has no normalized row
          // endpoint — it lives on that system's raw Output (nested under
          // `equipment_schedule`), where `position` is an [x, y] tuple and z is
          // storey-LOCAL. Split the tuple and lift z by the storey elevation
          // (0 when unknown) so glyphs stack at the same world levels as the
          // DB-lifted ducts/markers/mesh — the same treatment conduits get.
          const mechOut = await getMechanicalOutput(hvac.id).catch((err) => {
            console.error("mechanical equipment failed to load; continuing", err);
            return { equipment: [] };
          });
          if (cancelled) return;
          const elevationByStorey = new Map(storeys.map((s) => [s.id, s.elevation ?? 0] as const));
          const layerEquipment = (mechOut.equipment ?? []).map((e) => ({
            id: e.id,
            kind: e.kind,
            x_m: e.position[0],
            y_m: e.position[1],
            z_m: e.z_m + (elevationByStorey.get(e.storey_id) ?? 0),
            screening_only: e.screening_only,
            storey_id: e.storey_id,
          }));
          viewer.setMechanicalEquipment(layerEquipment);
          counts.mechanical = layerEquipment.length;
        }
        setEquipment(nextEquipment);
        setOverlay(counts);
        setServerPhase("ready");
      } catch (err) {
        if (cancelled) return;
        setServerPhase("error");
        setServerError(err instanceof Error ? err.message : String(err));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [modelId]);

  const onServerStoreyClick = useCallback(
    (storey: ApiStorey) => {
      const viewer = viewerRef.current;
      if (!viewer) return;
      if (activeStorey === storey.ifc_id) {
        setActiveStorey(null);
        void viewer.showAllItems();
      } else {
        setActiveStorey(storey.ifc_id);
        // Server storey id keeps the device/duct overlays in sync.
        void viewer.isolateStorey(storey.ifc_id, storey.id);
      }
    },
    [activeStorey],
  );

  const loadFile = useCallback(async (file: File) => {
    const viewer = viewerRef.current;
    if (!viewer) return;
    setError(null);
    setProgress({ phase: "opening", ratio: 0 });
    try {
      // Parse FIRST — an invalid upload must not wipe the currently shown model
      // or its overlays. Only once parsing succeeds do we replace the model and
      // clear the previous model's panel state.
      const parsed = await parseIfcFile(file, setProgress);
      setSummary(null);
      setActiveStorey(null);
      setServerPhase("idle");
      setServerStoreys([]);
      setOverlay(null);
      setEquipment([]);
      if (parsed.fragments !== null) {
        await viewer.loadModel(parsed.fragments, file.name, parsed.summary.hiddenElementIds ?? []);
      } else {
        // 3D (Fragments) conversion failed for this file (see
        // `evidence.viewer.threeD`) — the summary/2D data is still valid, so
        // skip the 3D load rather than crash. Clear any PREVIOUS file's 3D
        // model from the canvas first: without this, stale geometry from the
        // last successfully-loaded file would keep rendering alongside this
        // new file's stats/panels (already cleared above), a misleading
        // combination. A dedicated "3D unavailable" UX is a later task's
        // concern; for now just surface a message.
        await viewer.clearModel();
        console.error("IFC 3D conversion failed; showing 2D summary only, no 3D view");
        setError("3D view unavailable for this file — 2D summary data was still extracted.");
      }
      // Clear the previous model's generated overlays (devices/ducts/raceways)
      // only AFTER loadModel resolves (or is skipped because there is no 3D
      // model to load), so a loadModel failure doesn't leave the overlays
      // blanked behind a still-shown prior model (transactional clear).
      viewer.setDevices([]);
      viewer.setDucts([]);
      viewer.setRaceways([], []);
      viewer.setMembers([], [], []);
      viewer.setMechanicalEquipment([]);
      setSummary(parsed.summary);
    } catch (err) {
      // Parse/load failed: leave the prior model + overlays intact and surface
      // the error, rather than blanking a valid model behind a bad upload.
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setProgress(null);
    }
  }, []);

  const onFileChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      event.target.value = "";
      if (file) void loadFile(file);
    },
    [loadFile],
  );

  const onDrop = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      setDragging(false);
      if (progress) return;
      const file = Array.from(event.dataTransfer.files).find((f) =>
        f.name.toLowerCase().endsWith(".ifc"),
      );
      if (file) {
        void loadFile(file);
      } else if (event.dataTransfer.files.length > 0) {
        setError("Only .ifc files are supported.");
      }
    },
    [loadFile, progress],
  );

  const onStoreyClick = useCallback(
    (storeyId: number) => {
      const viewer = viewerRef.current;
      if (!viewer) return;
      if (activeStorey === storeyId) {
        setActiveStorey(null);
        void viewer.showAllItems();
      } else {
        setActiveStorey(storeyId);
        void viewer.isolateStorey(storeyId);
      }
    },
    [activeStorey],
  );

  const showAll = useCallback(() => {
    setActiveStorey(null);
    void viewerRef.current?.showAllItems();
  }, []);

  return (
    <div
      data-testid="ifc-drop-zone"
      onDragOver={(e) => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={onDrop}
      style={{
        display: "flex",
        width: "100vw",
        height: "100vh",
        overflow: "hidden",
        background: "#1a1a2e",
        color: "#d8d8e8",
        fontFamily: "sans-serif",
        outline: dragging ? "3px dashed #4a90d9" : "none",
        outlineOffset: -3,
      }}
    >
      <aside
        style={{
          width: 340,
          flexShrink: 0,
          overflowY: "auto",
          padding: "0.75rem",
          background: "#12121e",
          fontSize: "0.85rem",
        }}
      >
        {modelId && (
          <section data-testid="server-model-panel" style={panelSection}>
            <h2 style={{ ...sectionTitle, fontSize: "0.9rem" }}>Project model</h2>
            <nav style={{ marginBottom: "0.5rem" }}>
              <a href={`/models/${modelId}/mep`} style={{ color: "#8e8eb8" }}>
                ← MEP generation
              </a>
            </nav>
            {serverPhase === "loading" && (
              <p data-testid="server-model-loading" style={{ margin: 0 }}>
                Loading model from server…
              </p>
            )}
            {serverPhase === "error" && (
              <p data-testid="server-model-error" style={{ color: "#e06c75", margin: 0 }}>
                Could not load this model: {serverError}
              </p>
            )}
            {serverPhase === "ready" && serverStoreys.length > 0 && (
              <>
                <h3 style={sectionTitle}>Storeys</h3>
                <ul
                  data-testid="server-storey-list"
                  style={{ listStyle: "none", margin: 0, padding: 0 }}
                >
                  {serverStoreys.map((storey) => (
                    <li key={storey.id}>
                      <button
                        type="button"
                        onClick={() => onServerStoreyClick(storey)}
                        style={{
                          width: "100%",
                          textAlign: "left",
                          padding: "0.4rem 0.5rem",
                          marginBottom: 2,
                          borderRadius: 4,
                          border: "none",
                          cursor: "pointer",
                          background: activeStorey === storey.ifc_id ? "#4a90d9" : "#2a2a44",
                          color: activeStorey === storey.ifc_id ? "#fff" : "#d8d8e8",
                        }}
                      >
                        {storey.name}
                        {storey.elevation !== null && (
                          <span style={{ float: "right", opacity: 0.7 }}>
                            {storey.elevation.toFixed(2)} m
                          </span>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
                {activeStorey !== null && (
                  <button
                    type="button"
                    onClick={showAll}
                    style={{
                      marginTop: "0.4rem",
                      padding: "0.3rem 0.6rem",
                      borderRadius: 4,
                      border: "1px solid #8e8eb8",
                      background: "transparent",
                      color: "#d8d8e8",
                      cursor: "pointer",
                    }}
                  >
                    Show all storeys
                  </button>
                )}
              </>
            )}
            {serverPhase === "ready" && overlay && (
              <>
                <h3 style={sectionTitle}>Generated MEP</h3>
                {overlay.devices === 0 &&
                overlay.ducts === 0 &&
                overlay.equipment === 0 &&
                overlay.mechanical === 0 &&
                overlay.electrical === 0 &&
                overlay.conduits === 0 &&
                overlay.columns === 0 &&
                overlay.beams === 0 ? (
                  <p data-testid="mep-overlay-empty" style={{ margin: 0, color: "#8e8eb8" }}>
                    No generated systems yet — run a generation on the MEP page.
                  </p>
                ) : (
                  <div data-testid="mep-overlay-counts" style={{ display: "grid", gap: "0.3rem" }}>
                    {overlay.electrical > 0 && (
                      <label
                        data-testid="viewer-electrical-toggle"
                        style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem" }}
                      >
                        <input
                          type="checkbox"
                          checked={devicesVisible}
                          onChange={() => {
                            const next = !devicesVisible;
                            setDevicesVisible(next);
                            viewerRef.current?.setDeviceLayerVisible(next);
                          }}
                        />
                        Electrical devices ({overlay.electrical})
                      </label>
                    )}
                    {overlay.devices > 0 && (
                      <label
                        data-testid="viewer-devices-toggle"
                        style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem" }}
                      >
                        <input
                          type="checkbox"
                          checked={devicesVisible}
                          onChange={() => {
                            const next = !devicesVisible;
                            setDevicesVisible(next);
                            viewerRef.current?.setDeviceLayerVisible(next);
                          }}
                        />
                        Fire-alarm devices ({overlay.devices})
                      </label>
                    )}
                    {overlay.ducts > 0 && (
                      <label
                        data-testid="viewer-ducts-toggle"
                        style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem" }}
                      >
                        <input
                          type="checkbox"
                          checked={ductworkVisible}
                          onChange={() => {
                            const next = !ductworkVisible;
                            setDuctworkVisible(next);
                            viewerRef.current?.setDuctLayerVisible(next);
                          }}
                        />
                        Ductwork ({overlay.ducts} runs · {overlay.terminals} terminals)
                      </label>
                    )}
                    {overlay.mechanical > 0 && (
                      <label
                        data-testid="viewer-mechanical-toggle"
                        style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem" }}
                      >
                        <input
                          type="checkbox"
                          checked={mechanicalVisible}
                          onChange={() => {
                            const next = !mechanicalVisible;
                            setMechanicalVisible(next);
                            viewerRef.current?.setMechanicalLayerVisible(next);
                          }}
                        />
                        Equipment ({overlay.mechanical} placed)
                      </label>
                    )}
                    {overlay.columns + overlay.beams > 0 && (
                      <label
                        data-testid="viewer-members-toggle"
                        style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem" }}
                      >
                        <input
                          type="checkbox"
                          checked={membersVisible}
                          onChange={() => {
                            const next = !membersVisible;
                            setMembersVisible(next);
                            viewerRef.current?.setMemberLayerVisible(next);
                          }}
                        />
                        Members ({overlay.columns} columns · {overlay.beams} beams)
                      </label>
                    )}
                    <label
                      data-testid="viewer-shell-toggle"
                      style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem" }}
                    >
                      <input
                        type="checkbox"
                        checked={shellVisible}
                        onChange={() => {
                          const next = !shellVisible;
                          setShellVisible(next);
                          viewerRef.current?.setModelVisible(next);
                        }}
                      />
                      IFC shell
                    </label>
                    {overlay.conduits > 0 && (
                      <div>
                        <label
                          data-testid="viewer-raceways-toggle"
                          style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem" }}
                        >
                          <input
                            type="checkbox"
                            checked={racewaysVisible}
                            onChange={() => {
                              const next = !racewaysVisible;
                              setRacewaysVisible(next);
                              viewerRef.current?.setConduitLayerVisible(next);
                            }}
                          />
                          Raceways ({overlay.conduits} conduits · {overlay.pullBoxes} boxes)
                        </label>
                        {overlay.conduitKinds.length > 0 && (
                          <ul
                            data-testid="viewer-raceway-legend"
                            style={{
                              listStyle: "none",
                              margin: "0.2rem 0 0 1.4rem",
                              padding: 0,
                              color: "#8e8eb8",
                              fontSize: "0.78rem",
                            }}
                          >
                            {overlay.conduitKinds.map((kind) => (
                              <li
                                key={kind}
                                data-raceway-kind={kind}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "0.4rem",
                                  padding: "1px 0",
                                }}
                              >
                                <span
                                  aria-hidden
                                  style={{
                                    display: "inline-block",
                                    width: 12,
                                    height: 3,
                                    background: RACEWAY_COLORS[kind] ?? "#aab2c0",
                                  }}
                                />
                                {kind.replace(/_/g, " ").toUpperCase()}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                    {overlay.equipment > 0 && (
                      <ul
                        data-testid="viewer-equipment-list"
                        style={{
                          listStyle: "none",
                          margin: "0.2rem 0 0",
                          padding: 0,
                          color: "#8e8eb8",
                          fontSize: "0.78rem",
                        }}
                      >
                        {equipment.map((e) => {
                          const spec = equipmentMeshSpec(e);
                          return (
                            <li
                              key={e.id}
                              // The Three.js scene is canvas-only, so the AHU
                              // glyph has no DOM of its own. This list mirrors
                              // it 1:1 with `data-equipment-kind` so Playwright
                              // can count and assert against AHU placeholders
                              // (Task 15) without reading pixels.
                              data-equipment-kind={e.kind}
                              data-equipment-id={e.id}
                              data-equipment-screening-only={e.screening_only ? "true" : "false"}
                              style={{ padding: "1px 0" }}
                            >
                              {spec.label}
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                )}
              </>
            )}
          </section>
        )}

        <section style={panelSection}>
          <h2 style={{ ...sectionTitle, fontSize: "0.9rem" }}>IFC model</h2>
          <input
            ref={fileInputRef}
            data-testid="ifc-file-input"
            type="file"
            accept=".ifc"
            onChange={onFileChange}
            disabled={progress !== null}
            style={{ display: "none" }}
          />
          <button
            type="button"
            data-testid="ifc-load-button"
            onClick={() => fileInputRef.current?.click()}
            disabled={progress !== null}
            style={{
              width: "100%",
              padding: "0.5rem",
              borderRadius: 6,
              border: "1px solid #4a90d9",
              background: progress ? "#2a2a44" : "#4a90d9",
              color: "#fff",
              cursor: progress ? "wait" : "pointer",
            }}
          >
            {progress ? "Parsing…" : "Load IFC file"}
          </button>
          <p style={{ margin: "0.5rem 0 0", color: "#8e8eb8" }}>
            …or drag &amp; drop an .ifc file anywhere.
          </p>
          {progress && (
            <div data-testid="parse-progress" style={{ marginTop: "0.6rem" }}>
              <div style={{ marginBottom: "0.3rem" }}>{PHASE_LABEL[progress.phase]}</div>
              <div
                style={{
                  height: 6,
                  borderRadius: 3,
                  background: "#2a2a44",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    height: "100%",
                    width: `${Math.round(progress.ratio * 100)}%`,
                    background: "#4a90d9",
                    transition: "width 120ms linear",
                  }}
                />
              </div>
            </div>
          )}
          {error && (
            <p data-testid="parse-error" style={{ color: "#e06c75" }}>
              {error}
            </p>
          )}
        </section>

        {summary && (
          <>
            <section data-testid="model-stats" style={panelSection}>
              <h3 style={sectionTitle}>Model stats</h3>
              <div>
                <strong>{summary.fileName}</strong>
              </div>
              <div>Schema: {summary.schema}</div>
              <div>
                {summary.storeys.length} storeys · {summary.spaces.length} spaces ·{" "}
                {summary.totalEntities.toLocaleString()} entities
              </div>
              {summary.warnings.length > 0 && (
                <ul
                  data-testid="parse-warnings"
                  style={{
                    margin: "0.5rem 0 0",
                    paddingLeft: "1.1rem",
                    color: "#e5c07b",
                  }}
                >
                  {summary.warnings.map((w) => (
                    <li key={w}>{w}</li>
                  ))}
                </ul>
              )}
              <details style={{ marginTop: "0.5rem" }}>
                <summary style={{ cursor: "pointer", color: "#8e8eb8" }}>
                  Entity counts ({summary.entityCounts.length} classes)
                </summary>
                <div style={{ maxHeight: 180, overflowY: "auto" }}>
                  <table style={{ width: "100%", borderSpacing: 0 }}>
                    <tbody>
                      {summary.entityCounts.map((c) => (
                        <tr key={c.ifcClass}>
                          <td style={{ padding: "1px 0" }}>{c.ifcClass}</td>
                          <td style={{ textAlign: "right" }}>{c.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            </section>

            <section style={panelSection}>
              <h3 style={sectionTitle}>Storeys</h3>
              <ul data-testid="storey-list" style={{ listStyle: "none", margin: 0, padding: 0 }}>
                {selectableStoreys.map((storey) => (
                  <li key={storey.id}>
                    <button
                      type="button"
                      onClick={() => onStoreyClick(storey.id)}
                      style={{
                        width: "100%",
                        textAlign: "left",
                        padding: "0.4rem 0.5rem",
                        marginBottom: 2,
                        borderRadius: 4,
                        border: "none",
                        cursor: "pointer",
                        background: activeStorey === storey.id ? "#4a90d9" : "#2a2a44",
                        color: activeStorey === storey.id ? "#fff" : "#d8d8e8",
                      }}
                    >
                      {storey.name}
                      {storey.elevation !== null && (
                        <span style={{ float: "right", opacity: 0.7 }}>
                          {storey.elevation.toFixed(2)} m
                        </span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
              {activeStorey !== null && (
                <button
                  type="button"
                  onClick={showAll}
                  style={{
                    marginTop: "0.4rem",
                    padding: "0.3rem 0.6rem",
                    borderRadius: 4,
                    border: "1px solid #8e8eb8",
                    background: "transparent",
                    color: "#d8d8e8",
                    cursor: "pointer",
                  }}
                >
                  Show all storeys
                </button>
              )}
            </section>

            <section style={panelSection}>
              <h3 style={sectionTitle}>Spaces ({summary.spaces.length})</h3>
              <div style={{ maxHeight: 280, overflowY: "auto" }}>
                <table data-testid="spaces-table" style={{ width: "100%", borderSpacing: 0 }}>
                  <thead>
                    <tr style={{ color: "#8e8eb8", textAlign: "left" }}>
                      <th style={{ padding: "2px 4px" }}>Name</th>
                      <th style={{ padding: "2px 4px" }}>Storey</th>
                      <th style={{ padding: "2px 4px", textAlign: "right" }}>Area m²</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.spaces.map((space) => (
                      <tr key={space.id}>
                        <td style={{ padding: "2px 4px" }}>
                          {space.name}
                          {space.longName && space.longName !== space.name && (
                            <span style={{ color: "#8e8eb8" }}> {space.longName}</span>
                          )}
                        </td>
                        <td style={{ padding: "2px 4px" }}>{space.storeyName ?? "—"}</td>
                        <td style={{ padding: "2px 4px", textAlign: "right" }}>
                          {space.areaM2 !== null ? space.areaM2.toFixed(1) : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </>
        )}
      </aside>

      <div style={{ flex: 1, position: "relative", minWidth: 0 }}>
        <canvas
          ref={canvasRef}
          style={{ width: "100%", height: "100%", display: "block" }}
          data-testid="viewer-canvas"
        />
      </div>
    </div>
  );
}
